import type { TaskViewRequiredScope } from "@verbleif/lib/src/composables/Api/Type/TaskView/taskView.interface";
import type { TaskViewScopeParams } from "./Views/taskViewScopeQuery";

export type TaskViewKey = string;

export const BUILTIN_TASK_VIEW_KEYS = {
  ALL: "all",
  PINNED: "pinned",
  TODAY: "today",
  MY_UNTIL_TODAY: "my_until_today",
  ALL_TODAY: "all_today",
  ALL_UNTIL_TODAY: "all_until_today",
  ASSIGNED_TO_ME: "assigned_to_me",
  UNASSIGNED: "unassigned",
  ROUTE: "route",
  WALK_ROUTE: "walk_route",
  FINISHED: "finished",
  CHECKED: "checked",
  TASK_LIST: "task_list",
  PER_PROPERTY: "per_property",
} as const;

export type BuiltinTaskViewKey = typeof BUILTIN_TASK_VIEW_KEYS[keyof typeof BUILTIN_TASK_VIEW_KEYS];

const BUILTIN_SET = new Set<string>(Object.values(BUILTIN_TASK_VIEW_KEYS));

/**
 * Only for when the effective views (which carry the API's default view) could not be loaded:
 * the fixed built-in that the API also falls back to.
 */
export const TASK_VIEW_FALLBACK_KEY: TaskViewKey = BUILTIN_TASK_VIEW_KEYS.ALL;

export function isBuiltinTaskViewKey(value: string): value is BuiltinTaskViewKey {
  return BUILTIN_SET.has(value);
}

export function isKnownTaskViewKey(value: unknown, enabledKeys?: Iterable<string>): value is TaskViewKey {
  if (typeof value !== "string" || value === "") {
    return false;
  }
  if (enabledKeys) {
    return [...enabledKeys].includes(value);
  }
  return isBuiltinTaskViewKey(value);
}

/** @deprecated Use TaskViewKey + scope query params. Kept for permission shortcut mapping during migration. */
export enum TaskListType {
  TYPE_TODAY = "today",
  TYPE_ALL_TODAY = "all_today",
  TYPE_PER_PROPERTY = "property",
  TYPE_ALL = "all",
  TYPE_PINNED = "pinned",
  TYPE_ALL_UNTIL_TODAY = "all_until_today",
  TYPE_OPEN_UNTIL_TODAY = "my_until_today",
  TYPE_ASSIGNED_TO_ME = "assigned_to_me",
  TYPE_ROUTE = "route",
  TYPE_WALK_ROUTE = "walk_route",
  TYPE_UNASSIGNED = "unassigned",
  TYPE_READY = "finished",
  TYPE_FINISHED = "checked",
  TYPE_USER_GROUP = "user-group",
  TYPE_USER = "user",
}

export function taskListTypeToViewKey(type: TaskListType): TaskViewKey {
  if (type === TaskListType.TYPE_USER || type === TaskListType.TYPE_USER_GROUP || type === TaskListType.TYPE_PER_PROPERTY) {
    return BUILTIN_TASK_VIEW_KEYS.ALL;
  }
  return type as TaskViewKey;
}

/**
 * Whether the URL carries the scope a view requires, as the list worked before view management:
 * a task list only by its department. With view management on, use `hasTaskViewChoice`.
 */
export function taskViewRequiresScopeParam(
  requiredScope: TaskViewRequiredScope | null,
  scope: TaskViewScopeParams,
): boolean {
  if (!requiredScope) {
    return false;
  }
  if (requiredScope === "propertyRoute") {
    return scope.propertyRoute != null;
  }
  if (requiredScope === "taskList") {
    return scope.userGroup != null;
  }
  return scope.property != null;
}

/**
 * Whether a view is a row in Weergaven. Without view management a view that needs a scope (a walk
 * route, a task list, an object) has its own sidebar section instead; with it, it is a row that
 * opens the choice first.
 */
export function showsInViewsSection(view: { definition: { requiredScope?: TaskViewRequiredScope | null } }, viewManagement: boolean): boolean {
  return viewManagement || view.definition.requiredScope == null;
}

export function isTaskViewRequiredScope(value: unknown): value is TaskViewRequiredScope {
  return value === "taskList" || value === "property" || value === "propertyRoute";
}

/**
 * Whether the URL carries the choice of a view that asks for one (view management on): an object,
 * a walk route, or for a task list a department, a user inside it, or a user without a department.
 */
export function hasTaskViewChoice(requiredScope: TaskViewRequiredScope, scope: TaskViewScopeParams): boolean {
  if (requiredScope === "taskList") {
    return scope.userGroup != null || scope.user != null;
  }
  if (requiredScope === "propertyRoute") {
    return scope.propertyRoute != null;
  }
  return scope.property != null;
}
